import React, { useContext, useState } from 'react';
import { AuthContext } from './context/AuthContext';
import { ThemeContext } from './context/ThemeContext';
import Login from './auth/Login';
import AdminUserRegistrationForm from './auth/AdminUserRegistrationForm';
import BDDashboard from './dashboards/BDDashboard';
import SCDashboard from './dashboards/SCDashboard';
import AdminDashboard from './dashboards/AdminDashboard';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import EmailVerificationStatus from './auth/EmailVerificationStatus';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import {
  Box, Typography, Button, AppBar, Toolbar, CircularProgress, Paper,
  List, ListItem, ListItemButton, ListItemIcon, ListItemText, Drawer, IconButton, Tooltip
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import CategoryIcon from '@mui/icons-material/Category';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import LogoutIcon from '@mui/icons-material/Logout';
import Chatbot from './components/Chatbot';


function Sidebar({ isCollapsed, onToggleCollapse }) {
    const { user } = useContext(AuthContext);
    const location = useLocation();

    const menuItems = {
        ADMIN: [
            { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin-dashboard' },
            { text: 'Users', icon: <PeopleIcon />, path: '/admin-dashboard/users' },
            { text: 'Catalog', icon: <CategoryIcon />, path: '/admin-dashboard/catalog' },
            { text: 'Analytics', icon: <AnalyticsIcon />, path: '/admin-dashboard/analytics' },
        ],
        BUSINESS_DEVELOPMENT: [
            { text: 'Dashboard', icon: <DashboardIcon />, path: '/bd-dashboard' },
        ],
        SUPPLY_CHAIN: [
            { text: 'Dashboard', icon: <DashboardIcon />, path: '/sc-dashboard' },
        ],
    };

    const userMenuItems = user ? menuItems[user.role] || [] : [];

    return (
        <Drawer
            variant="permanent"
            sx={{
                width: isCollapsed ? 70 : 240,
                flexShrink: 0,
                transition: 'width 0.2s',
                [`& .MuiDrawer-paper`]: {
                    width: isCollapsed ? 70 : 240,
                    boxSizing: 'border-box',
                    backgroundColor: 'background.paper',
                    borderRight: '1px solid rgba(0, 0, 0, 0.12)',
                    transition: 'width 0.2s',
                    overflowX: 'hidden'
                },
            }}
        >
            <Toolbar sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: isCollapsed ? 'center' : 'space-between',
                px: 2,
              }}>
                {!isCollapsed && (
                    <Typography variant="h5" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                        eYantra EMS
                    </Typography>
                )}
                <IconButton onClick={onToggleCollapse}>
                    {isCollapsed ? <MenuIcon /> : <ChevronLeftIcon />}
                </IconButton>
            </Toolbar>
            <Box sx={{ overflow: 'auto', p: 1 }}>
                <List>
                    {userMenuItems.map((item) => (
                        <ListItem key={item.text} disablePadding>
                            <Tooltip title={isCollapsed ? item.text : ''} placement="right">
                                <ListItemButton component={Link} to={item.path} selected={location.pathname.startsWith(item.path)} sx={{ justifyContent: 'center' }}>
                                    <ListItemIcon sx={{ minWidth: 0, color: 'text.secondary' }}>{item.icon}</ListItemIcon>
                                    {!isCollapsed && <ListItemText primary={item.text} sx={{ ml: 2 }} />}
                                </ListItemButton>
                            </Tooltip>
                        </ListItem>
                    ))}
                </List>
            </Box>
        </Drawer>
    );
}

function MainLayout({ children }) {
    const { user, logout } = useContext(AuthContext);
    const { mode, toggleTheme } = useContext(ThemeContext);
    const [isSidebarCollapsed, setSidebarCollapsed] = useState(false);
    const handleLogout = () => logout();

    return (
        <Box sx={{ display: 'flex' }}>
            <Sidebar
                isCollapsed={isSidebarCollapsed}
                onToggleCollapse={() => setSidebarCollapsed(!isSidebarCollapsed)}
            />
            <Box component="main" sx={{ flexGrow: 1, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
                <AppBar position="static" color="default" elevation={0} sx={{ backgroundColor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
                    <Toolbar>
                        <Box sx={{ flexGrow: 1 }} />
                        <Typography sx={{ mr: 2 }}>Welcome, {user.username}</Typography>
                        <Tooltip title={`Toggle ${mode === 'dark' ? 'light' : 'dark'} mode`}>
                            <IconButton sx={{ mr: 2 }} onClick={toggleTheme} color="inherit">
                                {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
                            </IconButton>
                        </Tooltip>
                        <Button
                          variant="contained"
                          color="error"
                          startIcon={<LogoutIcon />}
                          onClick={handleLogout}
                        >
                          Logout
                        </Button>
                    </Toolbar>
                </AppBar>
                <Box sx={{ p: 3, flexGrow: 1, bgcolor: 'background.default' }}>
                    {children}
                </Box>
                <Chatbot/>
            </Box>
        </Box>
    );
}

function App() {
  const { user, isLoading } = useContext(AuthContext);

  const ProtectedRoute = ({ children, allowedRoles }) => {
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
        </Box>
      );
    }
    if (!user) {
      return <Navigate to="/login" replace />;
    }
    if (allowedRoles && !allowedRoles.includes(user.role)) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h4" color="error">Access Denied</Typography>
          <Typography>You do not have permission to view this page.</Typography>
        </Box>
      );
    }
    return <MainLayout>{children}</MainLayout>;
  };

   const AuthLayout = ({ children }) => (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Paper sx={{ p: 4, width: '100%', maxWidth: 420 }}>
        {children}
      </Paper>
    </Box>
  );

  return (
    <Router>
        <Routes>
            <Route path="/login" element={user ? <Navigate to="/" replace /> : <AuthLayout><Login /></AuthLayout>} />
            <Route path="/register" element={user ? <Navigate to="/" replace /> : <AuthLayout><AdminUserRegistrationForm /></AuthLayout>} />
            <Route path="/forgot-password" element={<AuthLayout><ForgotPassword /></AuthLayout>} />
            <Route path="/reset-password" element={<AuthLayout><ResetPassword /></AuthLayout>} />
            <Route path="/verify-email" element={<AuthLayout><EmailVerificationStatus /></AuthLayout>} />

            <Route
              path="/"
              element={
                isLoading ? <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box> :
                user ? (
                  user.role === 'BUSINESS_DEVELOPMENT' ? <Navigate to="/bd-dashboard" replace /> :
                  user.role === 'SUPPLY_CHAIN' ? <Navigate to="/sc-dashboard" replace /> :
                  user.role === 'ADMIN' ? <Navigate to="/admin-dashboard" replace /> :
                  <Typography>Invalid Role</Typography>
                ) : <Navigate to="/login" replace />
              }
            />

            <Route path="/bd-dashboard" element={<ProtectedRoute allowedRoles={['BUSINESS_DEVELOPMENT', 'ADMIN']}><BDDashboard /></ProtectedRoute>} />
            <Route path="/sc-dashboard" element={<ProtectedRoute allowedRoles={['SUPPLY_CHAIN', 'ADMIN']}><SCDashboard /></ProtectedRoute>} />
            <Route path="/admin-dashboard/*" element={<ProtectedRoute allowedRoles={['ADMIN']}><AdminDashboard /></ProtectedRoute>} />

            <Route path="*" element={<Typography sx={{p: 4, textAlign: 'center'}}>404 - Page Not Found</Typography>} />
        </Routes>
    </Router>
  );
}

export default App;